import React, { useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const ItemTypes = {
  TERM: 'term'
};

function DraggableTerm({ term, id }) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.TERM,
    item: { id, term },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }), [id, term]);

  return (
    <div
      ref={drag}
      className="p-2 bg-indigo-100 border rounded cursor-move mb-2"
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {term}
    </div>
  );
}

function DropTarget({ match, onDrop, matchedTerm }) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.TERM,
    drop: (item) => onDrop(item, match),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }), [match, matchedTerm]);

  return (
    <div
      ref={drop}
      className="p-2 border rounded min-h-[40px] bg-white mb-2"
      style={{ backgroundColor: isOver ? '#e0f2fe' : 'white' }}
    >
      <div className="text-sm text-gray-600 mb-1">{match}</div>
      {matchedTerm && (
        <div className="mt-1 p-1 bg-green-100 border rounded text-sm">{matchedTerm}</div>
      )}
    </div>
  );
}

export default function DragAndDropMatch({ config = {} }) {
  const { prompt = '', pairs = [] } = config;
  const [matches, setMatches] = useState({});
  const [result, setResult] = useState(null);

  const handleDrop = (item, matchText) => {
    setMatches((prev) => ({ ...prev, [matchText]: item.term }));
  };

  const handleCheck = () => {
    const correct = pairs.every((pair) => matches[pair.match] === pair.term);
    setResult(correct ? '✅ All matches correct!' : '❌ Some matches are incorrect.');
  };

  const handleReset = () => {
    setMatches({});
    setResult(null);
  };

  const unmatchedTerms = pairs
    .map((p) => p.term)
    .filter((term) => !Object.values(matches).includes(term));

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="max-w-4xl mx-auto">
        <h2 className="text-lg font-semibold mb-4">{prompt}</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-indigo-600 mb-2">🧩 Terms to drag</h3>
            {unmatchedTerms.map((term, idx) => (
              <DraggableTerm key={idx} id={idx} term={term} />
            ))}
          </div>

          <div>
            <h3 className="font-medium text-indigo-600 mb-2">🎯 Definitions</h3>
            {pairs.map((pair, idx) => (
              <DropTarget
                key={idx}
                match={pair.match}
                matchedTerm={matches[pair.match]}
                onDrop={handleDrop}
              />
            ))}
          </div>
        </div>

        <div className="mt-6 flex gap-4">
          <button
            onClick={handleCheck}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Check Answer
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Reset
          </button>
        </div>

        {result && <div className="mt-4 text-md font-medium text-indigo-700">{result}</div>}
      </div>
    </DndProvider>
  );
}
